import React, {useEffect, useState, useCallback} from "react";
import axios from "axios";
import {useAuth} from "../useAuth";
import {Review, ReviewRating} from "./objects/Review";
import {LanguageTaggedString} from "./objects/LanguageTaggedString";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Button} from "primereact/button";
import {Panel} from "primereact/panel";
import {ConfirmPopup, confirmPopup} from 'primereact/confirmpopup';
import {InputNumber} from "primereact/inputnumber";
import {Calendar} from "primereact/calendar";
import {Meta} from "./objects/Meta";
import {fetchObjects, createObject, deleteFromObjectsGrid} from "../services/CRUD";
import { Tooltip } from 'primereact/tooltip';

interface DataObject {
    deleted: boolean;
    id: string;
    meta: Meta;
    at_context?: string;
    at_type?: string;
    name: LanguageTaggedString[];
    itemReviewed?: string;
    reviewAspect?: string;
    reviewRating?: ReviewRating;
    reviewBody?: LanguageTaggedString[];
    author?: string;
    datePublished?: string;
    tags: { name: string; val: string }[];
}

const Reviews = () => {

    const {ogToken, ogAccessPoint} = useAuth();

    const [reviews, setReviews] = useState([]);
    const [newReviewDialogVisible, setNewReviewDialogVisible] = useState(false);

    /* Fields on new review form */
    const [names, setNames] = useState<{ value: string; language: string }[]>([]);
    const [itemReviewed, setItemReviewed] = useState('');
    const [reviewAspect, setReviewAspect] = useState('');
    const [reviewRatingType, setReviewRatingType] = useState('Rating');
    const [ratingValue, setRatingValue] = useState<number | null>(null);
    const [worstRating, setWorstRating] = useState<number | null>(1.0);
    const [bestRating, setBestRating] = useState<number | null>(5.0);
    const [ratingAspect, setRatingAspect] = useState('');
    const [reviewBodies, setReviewBodies] = useState<{ value: string; language: string }[]>([]);
    const [author, setAuthor] = useState('');
    const [datePublished, setDatePublished] = useState<Date | null>(null);

    const [tagNameValuePairs, setTagNameValuePairs] = useState<{ name: string, val: string }[]>([]);

    /* Fields used in the editing functionality */
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);

    const [showNameValidation, setShowNameValidation] = useState(false);
    const [showItemReviewedValidation, setShowItemReviewedValidation] = useState(false);
    const [showReviewAspectValidation, setShowReviewAspectValidation] = useState(false);
    const [showRatingValidation, setShowRatingValidation] = useState(false);
    const [showAuthorValidation, setShowAuthorValidation] = useState(false);
    const [showDatePublishedValidation, setShowDatePublishedValidation] = useState(false);
    const [showReviewBodyValidation, setShowReviewBodyValidation] = useState(false);
    const [showRatingAspectValidation, setShowRatingAspectValidation] = useState(false);

    /* A popup to show the user a command to get their reviews from a terminal */
    const [showCurlDialog, setShowCurlDialog] = useState(false);

    const objectsGridReviewsUrl = process.env.REACT_APP_OBJECTS_GRID_BASE_URL + '/reviews/';

    const fetchReviews = useCallback(async () => {
        fetchObjects(objectsGridReviewsUrl + "?from=0&size=1000",
            ogAccessPoint,
            ogToken)
            .then(responseArray => {
                setReviews(responseArray || []);
            }).catch(error => {
                console.error('Fetching reviews failed:', error);
                setReviews([]);
        });
    }, [ogAccessPoint, ogToken, objectsGridReviewsUrl]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews])

    const createReview = async () => {

        // Reset all validation states
        setShowNameValidation(false);
        setShowItemReviewedValidation(false);
        setShowReviewAspectValidation(false);
        setShowRatingValidation(false);
        setShowAuthorValidation(false);
        setShowDatePublishedValidation(false);

        // Validate names
        if (!names.length || names.some(n => !n.value)) {
            console.error('Name is required');
            setShowNameValidation(true);
            return;
        }

        // Validate item reviewed
        if (!itemReviewed.trim()) {
            console.error('Item Reviewed is required');
            setShowItemReviewedValidation(true);
            return;
        }

        // Validate review aspect
        if (!reviewAspect.trim()) {
            console.error('Review Aspect is required');
            setShowReviewAspectValidation(true);
            return;
        }

        // Validate rating
        if (!ratingValue || ratingValue < 0) {
            console.error('Rating Value is required');
            setShowRatingValidation(true);
            return;
        }

        // Validate author
        if (!author.trim()) {
            console.error('Author is required');
            setShowAuthorValidation(true);
            return;
        }

        // Validate date published
        if (!datePublished) {
            console.error('Date Published is required');
            setShowDatePublishedValidation(true);
            return;
        }

        // Validate review body
        if (!reviewBodies.length || reviewBodies.some(body => !body.value.trim())) {
            console.error('Review Body is required');
            setShowReviewBodyValidation(true);
            return;
        }

        // Validate rating aspect
        if (!ratingAspect.trim()) {
            console.error('Rating Aspect is required');
            setShowRatingAspectValidation(true);
            return;
        }

        const newReview: Review = {
            at_context: "https://schema.org",
            at_type: "Review",
            name: names.map(n => ({ at_value: n.value, at_language: n.language })),
            itemReviewed: itemReviewed || undefined,
            reviewAspect: reviewAspect || undefined,
            reviewRating: ratingValue ? {
                at_type: reviewRatingType,
                ratingValue: ratingValue,
                worstRating: worstRating || 1.0,
                bestRating: bestRating || 5.0,
                reviewAspect: ratingAspect
            } : undefined,
            reviewBody: reviewBodies.length > 0 ? reviewBodies.map(body => ({
                at_value: body.value,
                at_language: body.language
            })) : undefined,
            author: author || undefined,
            datePublished: datePublished ? datePublished.toISOString() : undefined,
            tags: tagNameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            }))
        };

         createObject(objectsGridReviewsUrl,
                ogAccessPoint,
                ogToken,
                newReview)
             .then(response => {
                setNewReviewDialogVisible(false);
                console.log("New Review created. Refreshing...");
                fetchReviews();
         }).catch(error => console.error('Adding review failed:', error));

    }

    // Helper function to render language-tagged strings
    const renderLanguageTaggedStrings = (strings: LanguageTaggedString[]) => {
        return strings.map((str, index) => (
            <div key={index}>{`${str.at_value} (${str.at_language})`}</div>
        ));
    };

    const renderName = (rowData: DataObject) => {
        return renderLanguageTaggedStrings(rowData.name);
    };

    const renderReviewBody = (rowData: DataObject) => {
        return rowData.reviewBody ? renderLanguageTaggedStrings(rowData.reviewBody) : null;
    };

    const renderReviewRating = (rowData: DataObject) => {
        if (!rowData.reviewRating) return null;
        const rating = rowData.reviewRating;
        return (
            <div>
                <div>Type: {rating.at_type}</div>
                <div>Value: {rating.ratingValue}</div>
                <div>Range: {rating.worstRating} - {rating.bestRating}</div>
                <div>Aspect: {rating.reviewAspect}</div>
            </div>
        );
    };

    const renderTags = (rowData: DataObject) => {
        return rowData.tags.map(attr => (
            <div key={attr.name}>{`${attr.name}: ${attr.val}`}</div>
        ));
    };

    // Add new name
    const addName = () => {
        setNames([...names, { value: '', language: 'en' }]);
    };



    // Remove name
    const removeName = (index: number) => {
        const updatedNames = names.filter((_, i) => i !== index);
        setNames(updatedNames);
    };

    // Add new review body
    const addReviewBody = () => {
        setReviewBodies([...reviewBodies, { value: '', language: 'en' }]);
    };



    // Remove review body
    const removeReviewBody = (index: number) => {
        const updatedBodies = reviewBodies.filter((_, i) => i !== index);
        setReviewBodies(updatedBodies);
    };

    // Add new name-value pair
    const addTagNameValuePair = () => {
        setTagNameValuePairs([...tagNameValuePairs, {name: '', val: ''}]);
    };

    // Update name-value pair
    const updateTagNameValuePair = (index: number, field: 'name' | 'val', value: string) => {
        const updatedPairs = [...tagNameValuePairs];
        updatedPairs[index][field] = value;
        setTagNameValuePairs(updatedPairs);
    };

    // Remove name-value pair
    const removeTagNameValuePair = (index: number) => {
        const updatedPairs = tagNameValuePairs.filter((_, i) => i !== index);
        setTagNameValuePairs(updatedPairs);
    };

    const tagsPanelHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold">Tags [Optional]</span>
                </div>
                <div>
                    <Button
                        icon="pi pi-plus"
                        label="Add New Tag"
                        onClick={addTagNameValuePair}
                    />
                </div>
            </div>
        );
    };

    const namesPanelHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold">Name [Required]</span>
                    <Button
                        icon="pi pi-info-circle"
                        className="p-button-text p-button-sm"
                        tooltip="The name of the item being reviewed. Name can be in multiple languages."
                    />
                </div>
                <div className="flex align-items-center gap-2">
                    <Button
                        icon="pi pi-plus"
                        label="Add New Name"
                        onClick={addName}
                    />
                </div>
            </div>
        );
    };

    const reviewBodiesPanelHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold">Review Bodies [Required]</span>
                    <Button
                        icon="pi pi-info-circle"
                        className="p-button-text p-button-sm"
                        tooltip="The actual body text of the review. One per language (allows for translations)."
                    />
                </div>
                <div className="flex align-items-center gap-2">
                    <Button
                        icon="pi pi-plus"
                        label="Add New Review Body"
                        onClick={addReviewBody}
                    />
                </div>
            </div>
        );
    };

    const handleEdit = (row: DataObject) => {
        console.log('Edit', row);

        setIsEditMode(true);
        setSelectedObjectId(row.id);
        
        // Set names (all)
        if (row.name && row.name.length > 0) {
            setNames(row.name.map(n => ({ value: n.at_value, language: n.at_language })));
        } else {
            setNames([]);
        }

        setItemReviewed(row.itemReviewed || '');
        setReviewAspect(row.reviewAspect || '');

        // Set review rating
        if (row.reviewRating) {
            setReviewRatingType(row.reviewRating.at_type);
            setRatingValue(row.reviewRating.ratingValue);
            setWorstRating(row.reviewRating.worstRating);
            setBestRating(row.reviewRating.bestRating);
            setRatingAspect(row.reviewRating.reviewAspect);
        } else {
            setReviewRatingType('Rating');
            setRatingValue(null);
            setWorstRating(1.0);
            setBestRating(5.0);
            setRatingAspect('');
        }

        // Set review bodies (all)
        if (row.reviewBody && row.reviewBody.length > 0) {
            setReviewBodies(row.reviewBody.map(b => ({ value: b.at_value, language: b.at_language })));
        } else {
            setReviewBodies([]);
        }

        setAuthor(row.author || '');
        setDatePublished(row.datePublished ? new Date(row.datePublished) : null);
        setTagNameValuePairs(row.tags);
        setNewReviewDialogVisible(true);
    };

    const updateReview = async () => {
        console.log("Update Review...");
        
        // Reset all validation states
        setShowNameValidation(false);
        setShowItemReviewedValidation(false);
        setShowReviewAspectValidation(false);
        setShowRatingValidation(false);
        setShowAuthorValidation(false);
        setShowDatePublishedValidation(false);

        // Validate names
        if (!names.length || names.some(n => !n.value)) {
            console.error('Name is required');
            setShowNameValidation(true);
            return;
        }

        // Validate item reviewed
        if (!itemReviewed.trim()) {
            console.error('Item Reviewed is required');
            setShowItemReviewedValidation(true);
            return;
        }

        // Validate review aspect
        if (!reviewAspect.trim()) {
            console.error('Review Aspect is required');
            setShowReviewAspectValidation(true);
            return;
        }

        // Validate rating
        if (!ratingValue || ratingValue < 0) {
            console.error('Rating Value is required');
            setShowRatingValidation(true);
            return;
        }

        // Validate author
        if (!author.trim()) {
            console.error('Author is required');
            setShowAuthorValidation(true);
            return;
        }

        // Validate date published
        if (!datePublished) {
            console.error('Date Published is required');
            setShowDatePublishedValidation(true);
            return;
        }

        // Validate review body
        if (!reviewBodies.length || reviewBodies.some(body => !body.value.trim())) {
            console.error('Review Body is required');
            setShowReviewBodyValidation(true);
            return;
        }

        // Validate rating aspect
        if (!ratingAspect.trim()) {
            console.error('Rating Aspect is required');
            setShowRatingAspectValidation(true);
            return;
        }

        const updatedReview: Review = {
            id: selectedObjectId!,
            at_context: "https://schema.org",
            at_type: "Review",
            name: names.map(n => ({ at_value: n.value, at_language: n.language })),
            itemReviewed: itemReviewed || undefined,
            reviewAspect: reviewAspect || undefined,
            reviewRating: ratingValue ? {
                at_type: reviewRatingType,
                ratingValue: ratingValue,
                worstRating: worstRating || 1.0,
                bestRating: bestRating || 5.0,
                reviewAspect: ratingAspect
            } : undefined,
            reviewBody: reviewBodies.length > 0 ? reviewBodies.map(body => ({
                at_value: body.value,
                at_language: body.language
            })) : undefined,
            author: author || undefined,
            datePublished: datePublished ? datePublished.toISOString() : undefined,
            tags: tagNameValuePairs.map(pair => ({
                name: pair.name,
                val: pair.val,
            })),
        };

        const updateUrl = objectsGridReviewsUrl + selectedObjectId;
        console.log(updateUrl);
        console.log('Updating review:', updatedReview);

        return axios.put(updateUrl,
            updatedReview, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-ObjectsGrid-AccessPoint': ogAccessPoint,
                    'Authorization': `Bearer ${ogToken}`
                }
            }).then(response => {
            console.log('Updated successfully:', response.data);
            setNewReviewDialogVisible(false);
            fetchReviews();
        }).catch(error => console.error('Updating review failed:', error));
    };

    const newReviewDialogFooterContent = (
        <div>
            <Button label="Cancel"
                    icon="pi pi-times"
                    onClick={() => setNewReviewDialogVisible(false)}
                    className="p-button-text"/>
            <Button
                label={isEditMode ? "Update" : "Create"}
                icon="pi pi-check"
                onClick={isEditMode ? updateReview : createReview}
                autoFocus
            />
        </div>
    );

    const closeDialog = () => {
        setNewReviewDialogVisible(false);
        setIsEditMode(false);
        setSelectedObjectId(null);
        setNames([]);
        setItemReviewed('');
        setReviewAspect('');
        setReviewRatingType('Rating');
        setRatingValue(null);
        setWorstRating(1.0);
        setBestRating(5.0);
        setRatingAspect('');
        setReviewBodies([]);
        setAuthor('');
        setDatePublished(null);
        setTagNameValuePairs([]);
        
        // Reset all validation states
        setShowNameValidation(false);
        setShowItemReviewedValidation(false);
        setShowReviewAspectValidation(false);
        setShowRatingValidation(false);
        setShowAuthorValidation(false);
        setShowDatePublishedValidation(false);
        setShowReviewBodyValidation(false);
        setShowRatingAspectValidation(false);
    };

    const handleDelete = (row: DataObject, event: React.MouseEvent<HTMLButtonElement>) => {

        confirmPopup({
            target: event.currentTarget,
            message: 'Do you want to delete this Review?',
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: () => {
                console.log('Delete', row);
                deleteReviewFromObjectsGrid(row).then((response) => {
                    console.log('Response from delete:', response);
                    fetchReviews();
                })
            },
            reject: () => {
                console.log('Cancel delete');
            }
        })
    };

    const deleteReviewFromObjectsGrid = async (row: DataObject) => {

        deleteFromObjectsGrid(objectsGridReviewsUrl + row.id,
            ogAccessPoint,
            ogToken)
            .then(response => {
                console.log('Deleted successfully:', response);

                // We need to wait a couple of seconds here. It takes OG a bit to delete the object.
                setTimeout(() => {
                    console.log('Refreshing Reviews after delete...');
                    fetchReviews();
                }, 2000);

            })
            .catch(error => {
               console.error('DeleteFailed:', error);
            });
    }

    const renderActions = (rowData: DataObject) => {
        return (
            <div className="flex gap-2">
                <Button icon="pi pi-pencil" rounded text aria-label="Edit" onClick={() => handleEdit(rowData)}/>
                <Button icon="pi pi-times" rounded text severity="danger" aria-label="Delete"
                        onClick={(e) => handleDelete(rowData, e)}/>
            </div>
        );
    };

    const reviewsCurlDialogHeader = (options: { className: any; }) => {
        const className = `${options.className} justify-content-space-between`;

        return (
            <div className={className}>
                <div className="flex align-items-center gap-2">
                    <span className="font-bold text-lg">Reviews</span>
                </div>
                <div>
                    <Button
                        icon="pi pi-code"
                        label="Show cURL"
                        onClick={() => setShowCurlDialog(true)}
                    />
                </div>
            </div>
        );
    };

    const curlCommand = `curl -X GET '${objectsGridReviewsUrl}' \\
                  -H "Authorization: Bearer ${ogToken}" \\
                  -H "X-ObjectsGrid-AccessPoint: ${ogAccessPoint}"`;

    return (
        <div>
            <Panel headerTemplate={reviewsCurlDialogHeader}>
                <Button
                    label="Create Review"
                    icon="pi pi-plus"
                    className="mb-2 mr-2"
                    onClick={() => {
                        setIsEditMode(false);
                        setSelectedObjectId(null);
                        setNames([]);
                        setItemReviewed('');
                        setReviewAspect('');
                        setReviewRatingType('Rating');
                        setRatingValue(null);
                        setWorstRating(1.0);
                        setBestRating(5.0);
                        setRatingAspect('');
                        setReviewBodies([]);
                        setAuthor('');
                        setDatePublished(null);
                        setTagNameValuePairs([]);
                        setNewReviewDialogVisible(true);
                    }}
                />
                <Button label="Refresh" icon="pi pi-refresh" className="mb-2" onClick={fetchReviews}/>
                <div className="datatable-container">
                    <DataTable value={reviews}
                               paginator
                               stripedRows
                               rows={100}
                                emptyMessage="No Reviews were found">
                        {/* Render columns */}
                        <Column field="id" header="ID"/>
                        <Column header="Name" body={renderName}/>
                        <Column field="itemReviewed" header="Item Reviewed"/>
                        <Column field="reviewAspect" header="Review Aspect"/>
                        <Column header="Review Rating" body={renderReviewRating}/>
                        <Column header="Review Body" body={renderReviewBody}/>
                        <Column field="author" header="Author"/>
                        <Column field="datePublished" header="Date Published"/>
                        <Column field="tags" header="Tags" body={renderTags}/>
                        <Column field="meta.createdAt" header="Created At"/>
                        <Column field="meta.createdBy" header="Created By"/>
                        <Column header="Actions" body={renderActions} style={{width: '120px'}}/>
                    </DataTable>
                </div>

                <Dialog header={isEditMode ? "Edit Review" : "Create New Review"}
                        visible={newReviewDialogVisible}
                        style={{ width: '70vw', maxWidth: '100vw', maxHeight: '100vh' }}
                        breakpoints={{ '960px': '90vw', '640px': '100vw' }}
                        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
                        onHide={closeDialog}
                        footer={newReviewDialogFooterContent}
                        modal>
                    <div className="p-fluid">
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <Panel headerTemplate={namesPanelHeader} className="mb-2">
                                    {names.map((n, idx) => (
                                        <div key={idx} className="flex align-items-center mb-2 gap-2">
                                            <InputText
                                                value={n.value}
                                                onChange={e => {
                                                    const newNames = [...names];
                                                    newNames[idx].value = e.target.value;
                                                    setNames(newNames);
                                                }}
                                                placeholder="Name"
                                                required
                                                style={{ width: '60%' }}
                                            />
                                            <InputText
                                                value={n.language}
                                                onChange={e => {
                                                    const newNames = [...names];
                                                    newNames[idx].language = e.target.value;
                                                    setNames(newNames);
                                                }}
                                                placeholder="en"
                                                style={{ width: '100px' }}
                                            />
                                            <Button icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={() => removeName(idx)} />
                                        </div>
                                    ))}
                                    {showNameValidation && <small className="p-error">At least one name is required and all names must have a value.</small>}
                                </Panel>

                                <div className="p-field mb-2">
                                    <label htmlFor="itemReviewed" className="flex align-items-center gap-2">
                                        Item Reviewed [Required]
                                        <Button
                                            icon="pi pi-info-circle"
                                            className="p-button-text p-button-sm"
                                            tooltip="The item that is being reviewed/rated. [The URI to the Item]"
                                        />
                                    </label>
                                    <InputText
                                        id="itemReviewed"
                                        value={itemReviewed}
                                        placeholder="https://www.somecompany.com/items/12345"
                                        onChange={(e) => setItemReviewed(e.target.value)}
                                        required
                                    />
                                    {showItemReviewedValidation && <small className="p-error">Item Reviewed is required</small>}
                                </div>

                                <div className="p-field mb-2">
                                    <label htmlFor="reviewAspect" className="flex align-items-center gap-2">
                                        Review Aspect [Required]
                                        <Button
                                            icon="pi pi-info-circle"
                                            className="p-button-text p-button-sm"
                                            tooltip="This Review or Rating is relevant to this part or facet of the itemReviewed. [FreeText]"
                                        />
                                    </label>
                                    <InputText
                                        id="reviewAspect"
                                        value={reviewAspect}
                                        placeholder="E.g. the quality of the item."
                                        onChange={(e) => setReviewAspect(e.target.value)}
                                        required
                                    />
                                    {showReviewAspectValidation && <small className="p-error">Review Aspect is required</small>}
                                </div>

                                <div className="p-field mb-2">
                                    <label htmlFor="author" className="flex align-items-center gap-2">
                                        Author [Required]
                                        <Button
                                            icon="pi pi-info-circle"
                                            className="p-button-text p-button-sm"
                                            tooltip="The author of the review. [The URI to the Author]"
                                        />
                                    </label>
                                    <InputText
                                        id="author"
                                        value={author}
                                        placeholder="https://www.somecompany.com/people/65412"
                                        onChange={(e) => setAuthor(e.target.value)}
                                        required
                                    />
                                    {showAuthorValidation && <small className="p-error">Author is required</small>}
                                </div>

                                <div className="p-field mb-2">
                                    <label htmlFor="datePublished">Date Published [Required]</label>
                                    <Calendar
                                        id="datePublished"
                                        value={datePublished}
                                        onChange={(e) => setDatePublished(e.value || null)}
                                        showIcon
                                        dateFormat="yy-mm-dd"
                                        required
                                    />
                                    {showDatePublishedValidation && <small className="p-error">Date Published is required</small>}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="p-field mb-2">
                                    <label htmlFor="reviewRatingType">Rating Type</label>
                                    <InputText
                                        id="reviewRatingType"
                                        value={reviewRatingType}
                                        onChange={(e) => setReviewRatingType(e.target.value)}
                                    />
                                </div>

                                <div className="p-field mb-2">
                                    <label htmlFor="ratingValue" className="flex align-items-center gap-2">
                                        Rating Value [Required]
                                        <Button
                                            icon="pi pi-info-circle"
                                            className="p-button-text p-button-sm"
                                            tooltip="The rating for the content. Use numbers 0-9 and full-stop '.' rather than comma ',' for decimels"
                                        />
                                    </label>
                                    <InputNumber
                                        id="ratingValue"
                                        value={ratingValue}
                                        onValueChange={(e) => setRatingValue(e.value || null)}
                                        min={0}
                                        max={10}
                                        step={0.1}
                                        required
                                    />
                                    {showRatingValidation && <small className="p-error">Rating Value is required</small>}
                                </div>

                                <div className="p-field mb-2">
                                    <label htmlFor="worstRating" className="flex align-items-center gap-2">
                                        Worst Rating [Required]
                                        <Button
                                            icon="pi pi-info-circle"
                                            className="p-button-text p-button-sm"
                                            tooltip="The lowest value allowed in this rating system - an decimal number."
                                        />
                                    </label>
                                    <InputNumber
                                        id="worstRating"
                                        value={worstRating}
                                        onValueChange={(e) => setWorstRating(e.value || null)}
                                        min={0}
                                        max={10}
                                        step={0.1}
                                    />
                                </div>

                                <div className="p-field mb-2">
                                    <label htmlFor="bestRating" className="flex align-items-center gap-2">
                                        Best Rating [Required]
                                        <Button
                                            icon="pi pi-info-circle"
                                            className="p-button-text p-button-sm"
                                            tooltip="The highest value allowed in this rating system - an decimal number."
                                        />
                                    </label>
                                    <InputNumber
                                        id="bestRating"
                                        value={bestRating}
                                        onValueChange={(e) => setBestRating(e.value || null)}
                                        min={0}
                                        max={10}
                                        step={0.1}
                                    />
                                </div>

                                <div className="p-field mb-2">
                                    <label htmlFor="ratingAspect">Rating Aspect [Required]</label>
                                    <InputText
                                        id="ratingAspect"
                                        value={ratingAspect}
                                        onChange={(e) => setRatingAspect(e.target.value)}
                                        required
                                    />
                                    {showRatingAspectValidation && <small className="p-error">Rating Aspect is required</small>}
                                </div>
                            </div>
                        </div>

                        <Panel headerTemplate={reviewBodiesPanelHeader} className="mb-2">
                            {reviewBodies.map((body, idx) => (
                                <div key={idx} className="flex align-items-center mb-2 gap-2">
                                    <InputTextarea
                                        value={body.value}
                                        onChange={e => {
                                            const newBodies = [...reviewBodies];
                                            newBodies[idx].value = e.target.value;
                                            setReviewBodies(newBodies);
                                        }}
                                        placeholder="Review body text"
                                        rows={3}
                                        autoResize
                                        style={{ width: '60%' }}
                                        required
                                    />
                                    <InputText
                                        value={body.language}
                                        onChange={e => {
                                            const newBodies = [...reviewBodies];
                                            newBodies[idx].language = e.target.value;
                                            setReviewBodies(newBodies);
                                        }}
                                        placeholder="en"
                                        style={{ width: '100px' }}
                                    />
                                    <Button icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={() => removeReviewBody(idx)} />
                                </div>
                            ))}
                            {showReviewBodyValidation && <small className="p-error">At least one Review Body is required and all review bodies must have content.</small>}
                        </Panel>

                        <Panel headerTemplate={tagsPanelHeader} className="mb-2">
                            {tagNameValuePairs.map((pair, index) => (
                                <div key={index} className="p-d-flex p-ai-center p-jc-between p-mb-2 mb-2">
                                    <InputText
                                        placeholder="Name"
                                        value={pair.name}
                                        onChange={(e) => updateTagNameValuePair(index, 'name', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <InputText
                                        placeholder="Value"
                                        value={pair.val}
                                        onChange={(e) => updateTagNameValuePair(index, 'val', e.target.value)}
                                        style={{marginRight: '1rem', width: '150px'}}
                                    />
                                    <Button
                                        icon="pi pi-times"
                                        className="p-button-danger p-button-outlined"
                                        onClick={() => removeTagNameValuePair(index)}
                                    />
                                </div>
                            ))}
                        </Panel>

                    </div>
                </Dialog>

                <ConfirmPopup/>
                <Tooltip />

            </Panel>
            <Dialog
                header="cURL Command"
                visible={showCurlDialog}
                style={{width: '60vw'}}
                onHide={() => setShowCurlDialog(false)}
            >
                <div>
                    <p>You can use this command to get your Reviews from a terminal...</p>
                    <pre className="bg-gray-100 p-3 rounded overflow-auto">
                                <code>{curlCommand}</code>
                            </pre>
                    <Button
                        label="Copy to Clipboard"
                        icon="pi pi-copy"
                        onClick={() => navigator.clipboard.writeText(curlCommand)}
                        className="mt-2"
                    />
                </div>
            </Dialog>
        </div>);

};

export default Reviews; 